import React, { FC, useState } from "react";
import "../css/setting-text-field.css";

interface SetInputProp {
  placeholderTxt?: string;
  width?: number;
  value?: string;
  onChange?: (value: string) => void;
  validate?: boolean;
  validateMode?: "mb" | "jobCode";
  type?: "text" | "number";
}

const SettingInput: FC<SetInputProp> = ({
  placeholderTxt,
  width = 120,
  value,
  onChange,
  validate = false,
  validateMode = "mb",
  type = "text",
}) => {
  const [error, setError] = useState<string | null>(null);

  const validateJobCode = (raw: string) => {
    const v = raw;

    /* istanbul ignore next */
    if (!v) return null;

    // block any braces/brackets
    if (/[{}\[\]]/.test(v)) return "Do not use { } or [ ]"; //NOSONAR

    // allow only safe chars (adjust if you need dots/slashes/etc)
    if (!/^[A-Za-z0-9 _-]+$/.test(v)) {
      return "Only letters, numbers, spaces, '_' and '-' are allowed";
    }

    // optional extra rules
    if (v.trim() !== v) return "No leading/trailing spaces";
    if (/\s{2,}/.test(v)) return "Avoid double spaces";
    if (/--/.test(v)) return "Avoid double hyphens";

    return null;
  };

  const validateMb = (raw: string) => {
    /* istanbul ignore next */
    if (!raw) return null;
    if (!/^\d+(\s*mb)?$/i.test(raw)) return "Only numbers or format like '10 MB' allowed";
    return null;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    onChange?.(raw);

    if (!validate || type === "number") return;

    const err = validateMode === "jobCode" ? validateJobCode(raw) : validateMb(raw);
    setError(err);
  };

  const handleBlur = () => {
    if (!validate || !value || type === "number") return;

    if (validateMode === "jobCode") {
      // no auto-formatting, just re-check
      setError(validateJobCode(value));
      return;
    }

    // existing MB formatting behavior
    const trimmed = value.trim();

    if (!/^\d+/i.test(trimmed)) {
      setError("Must be a number");
      return;
    }

    if (/^\d+$/i.test(trimmed)) {
      onChange?.(`${trimmed} MB`);
      setError(null);
      return;
    }

    if (!/^\d+\s*mb$/i.test(trimmed)) {
      setError("Format must be a number followed by MB, e.g. '10 MB'");
    } else {
      setError(null);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <input
        className={`scl--cp-inp-sing-box-sty ${error ? "error" : ""}`}
        type={type}
        placeholder={placeholderTxt}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        style={{ width: `${width}px` }}
      />
    </div>
  );
};

export default SettingInput;